import React from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { SHELL } from "@/constants/testIds";
import { LayoutDashboard, Table2, History, Settings as SettingsIcon, Mail, LogOut, Sparkles } from "lucide-react";

const NavItem = ({ to, icon, label, testid }) => (
  <NavLink
    to={to}
    data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
        isActive
          ? "bg-[#F3F1EC] text-[#1C2321] font-medium"
          : "text-[#5D6D63] hover:bg-[#F3F1EC] hover:text-[#1C2321]"
      }`
    }
  >
    <span className="h-4 w-4">{icon}</span>
    {label}
  </NavLink>
);

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const b = user?.billing;
  const showTrialBanner = b && !b.active && b.status !== "trialing" && !user?.demo_mode;
  const daysLeft = b?.trial_ends_at ? Math.max(0, Math.ceil((new Date(b.trial_ends_at) - new Date()) / 86400000)) : null;
  const showTrialCountdown = b?.status === "trialing" && daysLeft !== null && !user?.demo_mode;

  return (
    <div className="min-h-screen flex bg-[#F9F8F6]">
      <aside data-testid={SHELL.sidebar} className="w-64 shrink-0 border-r border-[#E8E6DF] bg-white flex flex-col">
        <div className="px-5 py-6 border-b border-[#E8E6DF]">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-[#971a8c] text-white flex items-center justify-center">
              <Mail className="h-4 w-4" />
            </div>
            <span className="font-heading text-lg text-[#1C2321]">ContactAssist</span>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          <NavItem to="/dashboard" icon={<LayoutDashboard className="h-4 w-4" />} label="Dashboard" testid={SHELL.navDashboard} />
          <NavItem to="/log" icon={<Table2 className="h-4 w-4" />} label="Contact Log" testid={SHELL.navLog} />
          <NavItem to="/runs" icon={<History className="h-4 w-4" />} label="Run History" testid={SHELL.navRuns} />
          <NavItem to="/settings" icon={<SettingsIcon className="h-4 w-4" />} label="Settings" testid={SHELL.navSettings} />
        </nav>
        <div className="p-3 border-t border-[#E8E6DF]">
          <div className="flex items-center gap-3 px-2 py-2">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-[#F3F1EC]" />
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-[#1C2321] truncate">{user?.name || "Teacher"}</div>
              <div className="text-xs text-[#8B9891] truncate">{user?.email}</div>
            </div>
            <button
              data-testid={SHELL.logoutBtn}
              onClick={logout}
              aria-label="Sign out"
              className="h-8 w-8 rounded-md text-[#5D6D63] hover:bg-[#F3F1EC] hover:text-[#1C2321] flex items-center justify-center transition-colors"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        {user?.demo_mode && (
          <div data-testid="demo-mode-banner" className="w-full bg-[#26abe2] text-white text-sm px-6 py-2 text-center">
            <Sparkles className="inline h-3.5 w-3.5 mr-2" />
            You&apos;re in demo mode with sample parent emails · Real Gmail is not connected
          </div>
        )}
        {showTrialCountdown && (
          <div data-testid="trial-countdown-banner" className="w-full bg-[#fdf4e7] border-b border-[#f0c98a] text-sm px-6 py-2 text-center text-[#5c3a00]">
            You have <strong>{daysLeft} day{daysLeft === 1 ? "" : "s"}</strong> left in your free trial ·
            <Link to="/settings" className="ml-2 underline font-medium">Manage billing</Link>
          </div>
        )}
        {showTrialBanner && (
          <div data-testid="paywall-banner" className="w-full bg-[#fde5ef] border-b border-[#ec197f] text-sm px-6 py-2 text-center text-[#1C2321]">
            <strong>Your trial has ended.</strong> Scans and AI features are paused —
            <Link to="/pricing" className="ml-2 underline font-medium text-[#ec197f]">restart your subscription</Link>
          </div>
        )}
        <div className="max-w-[1600px] mx-auto px-8 py-10 fade-in">{children}</div>
      </main>
    </div>
  );
}
